import React, { useState } from 'react'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button/Button'
import { EditPencilIcon } from '@sales-center/components'
import { ExpandableReason } from '@user/features/MapView/KnockStatusModal/ExpandableReason'

type Props = {
  reason: string | null
  canEdit: boolean
  isSaving: boolean
  onSave: (newReason: string | null) => Promise<boolean>
}

export function EditableKnockStatusReason({
  reason,
  canEdit,
  isSaving,
  onSave,
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [editReason, setEditReason] = useState('')

  const handleStartEdit = () => {
    setEditReason(reason || '')
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setEditReason('')
  }

  const handleSaveEdit = async () => {
    const sanitizedReason = editReason.trim() || null
    const success = await onSave(sanitizedReason)
    if (success) {
      setIsEditing(false)
      setEditReason('')
    }
  }

  if (isEditing) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ mt: 0.4 }}>
          <TextField
            fullWidth
            label="Reason"
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            disabled={isSaving}
            multiline
            inputProps={{
              maxLength: 200,
            }}
            minRows={1}
            maxRows={2}
            helperText={`${editReason.length}/200`}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 0.5,
            mt: 0.8,
          }}
        >
          <Button
            variant="text"
            type="button"
            onClick={handleCancelEdit}
            disabled={isSaving}
            sx={{
              color: '#1565C0',
              fontWeight: 400,
              letterSpacing: '0.15px',
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="text"
            onClick={handleSaveEdit}
            disabled={isSaving}
            sx={{
              color: '#1565C0',
              fontWeight: 400,
              letterSpacing: '0.15px',
            }}
          >
            Save
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {canEdit && (
        <IconButton
          aria-label="Edit knock status reason"
          // top: -4 lifts the pencil glyph toward the "Reason:" line instead of
          // drooping below it under the medium button's padding (SH-1817).
          sx={{
            position: 'absolute',
            right: 0,
            top: -4,
            zIndex: 1,
          }}
          onClick={handleStartEdit}
          size="medium"
        >
          <EditPencilIcon />
        </IconButton>
      )}

      <ExpandableReason
        reason={reason || '-'}
        reservedRight={canEdit ? 40 : 0}
      />
    </Box>
  )
}
